import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { apiService } from '../services/apiService';
import { api } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import GenerateInvoicesModal from '../components/GenerateInvoicesModal';
import { formatDate, formatCurrency } from '../utils/dateUtils';
import toast from 'react-hot-toast';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ===== DATE RANGE PICKER =====
const PRESETS = [
  { label: 'Hoje', key: 'today' },
  { label: 'Ontem', key: 'yesterday' },
  { label: 'Esta semana', key: 'this_week' },
  { label: 'Semana passada', key: 'last_week' },
  { label: 'Este mês', key: 'this_month' },
  { label: 'Mês passado', key: 'last_month' },
  { label: 'Últimos 7 dias', key: 'last_7' },
  { label: 'Últimos 30 dias', key: 'last_30' },
  { label: 'Últimos 90 dias', key: 'last_90' },
  { label: 'Próximo mês', key: 'next_month' },
  { label: 'Próximos 60 dias', key: 'next_60' },
  { label: 'Próximos 90 dias', key: 'next_90' },
  { label: 'Personalizado', key: 'custom' },
];

function getPresetRange(key) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];

  const startOfWeek = (d) => {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
  };

  switch (key) {
    case 'today': {
      const f = fmt(today);
      return { from: f, to: f };
    }
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(today.getDate() - 1);
      const f = fmt(y);
      return { from: f, to: f };
    }
    case 'this_week': {
      const start = startOfWeek(new Date(today));
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { from: fmt(start), to: fmt(end) };
    }
    case 'last_week': {
      const thisMonday = startOfWeek(new Date(today));
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(thisMonday.getDate() - 7);
      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastMonday.getDate() + 6);
      return { from: fmt(lastMonday), to: fmt(lastSunday) };
    }
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: fmt(start), to: fmt(end) };
    }
    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: fmt(start), to: fmt(end) };
    }
    case 'last_7': {
      const from = new Date(today);
      from.setDate(today.getDate() - 6);
      return { from: fmt(from), to: fmt(today) };
    }
    case 'last_30': {
      const from = new Date(today);
      from.setDate(today.getDate() - 29);
      return { from: fmt(from), to: fmt(today) };
    }
    case 'last_90': {
      const from = new Date(today);
      from.setDate(today.getDate() - 89);
      return { from: fmt(from), to: fmt(today) };
    }
    case 'next_month': {
      const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return { from: fmt(start), to: fmt(end) };
    }
    case 'next_60': {
      const from = new Date(today);
      from.setDate(today.getDate() + 1);
      const to = new Date(today);
      to.setDate(today.getDate() + 60);
      return { from: fmt(from), to: fmt(to) };
    }
    case 'next_90': {
      const from = new Date(today);
      from.setDate(today.getDate() + 1);
      const to = new Date(today);
      to.setDate(today.getDate() + 90);
      return { from: fmt(from), to: fmt(to) };
    }
    default:
      return { from: '', to: '' };
  }
}

function DateRangePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(value.from || '');
  const [customTo, setCustomTo] = useState(value.to || '');
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectPreset = (key) => {
    if (key === 'custom') {
      onChange({ preset: 'custom', from: customFrom, to: customTo });
      return;
    }
    const range = getPresetRange(key);
    setCustomFrom(range.from);
    setCustomTo(range.to);
    onChange({ preset: key, from: range.from, to: range.to });
    setOpen(false);
  };

  const applyCustom = () => {
    onChange({ preset: 'custom', from: customFrom, to: customTo });
    setOpen(false);
  };

  const formatLabel = () => {
    if (!value.preset && !value.from) return 'Todas as datas';
    if (value.preset && value.preset !== 'custom') {
      return PRESETS.find(p => p.key === value.preset)?.label || 'Todas as datas';
    }
    if (value.from && value.to) {
      const fmt = (s) => {
        if (!s) return '';
        const [y, m, d] = s.split('-');
        return `${d}/${m}/${y}`;
      };
      return `${fmt(value.from)} – ${fmt(value.to)}`;
    }
    if (value.from) return `A partir de ${value.from}`;
    return 'Todas as datas';
  };

  const activePreset = value.preset;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left flex items-center justify-between gap-2 hover:border-blue-400 transition"
      >
        <span className="text-sm truncate">{formatLabel()}</span>
        <svg className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg min-w-[280px]">
          <div className="p-2">
            {PRESETS.filter(p => p.key !== 'custom').map(preset => (
              <button
                key={preset.key}
                type="button"
                onClick={() => selectPreset(preset.key)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                  activePreset === preset.key
                    ? 'bg-blue-600 text-white font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {preset.label}
              </button>
            ))}
            <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
              <p className="text-xs text-gray-500 dark:text-gray-400 px-3 mb-2 font-medium uppercase tracking-wide">Personalizado</p>
              <div className="px-3 space-y-2">
                <div className="flex gap-2 items-center">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="De"
                  />
                  <span className="text-gray-400 text-sm">–</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Até"
                  />
                </div>
                <button
                  type="button"
                  onClick={applyCustom}
                  className="w-full py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition"
                >
                  Aplicar
                </button>
              </div>
            </div>
            {(value.preset || value.from) && (
              <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2 px-2">
                <button
                  type="button"
                  onClick={() => { onChange({ preset: '', from: '', to: '' }); setOpen(false); }}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                >
                  Limpar filtro de data
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== SORT HEADER =====
function SortTh({ col, label, sortColumn, sortDirection, onSort, className = '' }) {
  const active = sortColumn === col;
  return (
    <th
      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition ${className}`}
      onClick={() => onSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        <span className="text-gray-400">
          {active ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </span>
    </th>
  );
}

// ===== MAIN PAGE =====
const InvoicesPage = () => {
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [plateFilter, setPlateFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [dateRange, setDateRange] = useState({ preset: '', from: '', to: '' });
  const [paymentDateRange, setPaymentDateRange] = useState({ preset: '', from: '', to: '' });
  const [sortColumn, setSortColumn] = useState('due_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [editingDueDate, setEditingDueDate] = useState(null);
  const [newDueDateValue, setNewDueDateValue] = useState('');
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [bulkDueDateModal, setBulkDueDateModal] = useState(false);
  const [bulkDueDateRows, setBulkDueDateRows] = useState([]);
  const [savingBulkDueDate, setSavingBulkDueDate] = useState(false);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelJustification, setCancelJustification] = useState('');
  const [cancellingInvoice, setCancellingInvoice] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Modal unificado de edição
  const [editModal, setEditModal] = useState(null); // { invoice, items, original }
  const [editForm, setEditForm] = useState({});
  const [editItems, setEditItems] = useState([]);
  const [editBankAccounts, setEditBankAccounts] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [extrajudicialModal, setExtrajudicialModal] = useState(null); // invoice
  const [extrajudicialData, setExtrajudicialData] = useState(null);
  const [loadingExtrajudicial, setLoadingExtrajudicial] = useState(false);
  const [sendingExtrajudicial, setSendingExtrajudicial] = useState(false);
  const [extrajudicialStatusModal, setExtrajudicialStatusModal] = useState(null); // { invoice, loading, data }


  useEffect(() => {
    loadInvoices();
  }, [statusFilter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const response = await apiService.getInvoices(params);
      setInvoices(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar faturas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const filteredAndSorted = [...invoices]
    .filter(inv => {
      if (plateFilter && !inv.vehicle_plate?.toLowerCase().includes(plateFilter.toLowerCase())) return false;
      if (clientFilter && !inv.client_name?.toLowerCase().includes(clientFilter.toLowerCase())) return false;
      if (dateRange.from) {
        const due = inv.due_date ? inv.due_date.split('T')[0] : '';
        if (due < dateRange.from) return false;
      }
      if (dateRange.to) {
        const due = inv.due_date ? inv.due_date.split('T')[0] : '';
        if (due > dateRange.to) return false;
      }
      if (paymentDateRange.from) {
        const pd = inv.payment_date ? inv.payment_date.split('T')[0] : '';
        if (!pd || pd < paymentDateRange.from) return false;
      }
      if (paymentDateRange.to) {
        const pd = inv.payment_date ? inv.payment_date.split('T')[0] : '';
        if (!pd || pd > paymentDateRange.to) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let aVal = a[sortColumn] ?? '';
      let bVal = b[sortColumn] ?? '';

      // Numeric columns
      if (sortColumn === 'total_value') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal + '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const hasActiveFilters = statusFilter || plateFilter || clientFilter || dateRange.from || dateRange.to || paymentDateRange.from || paymentDateRange.to;

  // Cards refletem os filtros ativos (se houver filtro, usa filteredAndSorted; senão, usa todas)
  const statsBase = hasActiveFilters ? filteredAndSorted : invoices;
  const statistics = {
    total: statsBase.length,
    pending: statsBase.filter(i => (i.status || '').toUpperCase() === 'PENDING').length,
    paid:    statsBase.filter(i => (i.status || '').toUpperCase() === 'PAID').length,
    overdue: statsBase.filter(i => (i.status || '').toUpperCase() === 'OVERDUE').length,
    total_pending:  statsBase.filter(i => (i.status || '').toUpperCase() === 'PENDING').reduce((s, i) => s + parseFloat(i.total_value || 0), 0),
    total_received: statsBase.filter(i => (i.status || '').toUpperCase() === 'PAID').reduce((s, i)    => s + parseFloat(i.total_value || 0), 0),
    total_overdue:  statsBase.filter(i => (i.status || '').toUpperCase() === 'OVERDUE').reduce((s, i) => s + parseFloat(i.total_value || 0), 0),
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { color: 'bg-yellow-100 text-yellow-800', label: '⏳ Pendente' },
      'PAID': { color: 'bg-green-100 text-green-800', label: '✅ Paga' },
      'OVERDUE': { color: 'bg-red-100 text-red-800', label: '🔴 Vencida' },
      'CANCELLED': { color: 'bg-gray-100 text-gray-800', label: '⚫ Cancelada' }
    };
    const config = statusConfig[(status || '').toUpperCase()] || statusConfig['PENDING'];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const handleGenerateSuccess = () => {
    loadInvoices();
  };

  const handleQuickAction = async (invoiceId, action, asaasChargeId = null) => {
    try {
      switch (action) {
        case 'finalize':
          await apiService.finalizeInvoice(invoiceId);
          toast.success('Fatura finalizada (Asaas + PDF)');
          break;
        case 'generate-pdf':
          await apiService.generateInvoicePDF(invoiceId);
          toast.success('PDF gerado com sucesso');
          break;
        case 'regenerate-pdf':
          await apiService.regenerateInvoicePDF(invoiceId);
          toast.success('PDF regerado com sucesso');
          break;
        case 'email':
          await apiService.sendInvoiceEmail(invoiceId);
          toast.success('Email enviado com sucesso');
          break;
        case 'whatsapp':
          await apiService.sendInvoiceWhatsApp(invoiceId);
          toast.success('WhatsApp enviado com sucesso');
          break;
        case 'paid':
          // Handled by markPaidModal — should not reach here
          break;
        case 'cancel-asaas':
          if (!asaasChargeId) {
            toast.error('Fatura não possui cobrança Asaas para cancelar');
            return;
          }
          if (!confirm('⚠️ Confirma o cancelamento deste boleto no Asaas? Esta ação não pode ser desfeita.')) {
            return;
          }
          await apiService.cancelAsaasCharge(asaasChargeId);
          toast.success('Boleto Asaas cancelado com sucesso');
          break;
        case 'delete':
          // Handled by cancelModal — do not reach here
          break;
        case 'edit-due-date':
          return;
      }
      loadInvoices();
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || error.message;
      toast.error(msg || 'Erro desconhecido');
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setPlateFilter('');
    setClientFilter('');
    setDateRange({ preset: '', from: '', to: '' });
    setPaymentDateRange({ preset: '', from: '', to: '' });
  };

  // Seleção de linhas
  const exportTarget = selectedIds.size > 0
    ? filteredAndSorted.filter(i => selectedIds.has(i.id))
    : filteredAndSorted;

  const allSelected = filteredAndSorted.length > 0 && filteredAndSorted.every(i => selectedIds.has(i.id));
  const someSelected = filteredAndSorted.some(i => selectedIds.has(i.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAndSorted.map(i => i.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Ações em lote ───────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (!window.confirm(`Excluir ${selectedIds.size} fatura(s) selecionada(s)?`)) return;
    const toastId = toast.loading(`Excluindo ${selectedIds.size} fatura(s)...`);
    let ok = 0;
    for (const id of selectedIds) {
      try { await apiService.cancelInvoice(id); ok++; } catch {}
    }
    toast.success(`${ok} fatura(s) excluída(s)`, { id: toastId });
    setSelectedIds(new Set());
    fetchInvoices();
  };

  const openBulkDueDateModal = () => {
    const selected = filteredAndSorted.filter(i => selectedIds.has(i.id));
    setBulkDueDateRows(selected.map(inv => ({
      id: inv.id,
      invoice_number: inv.invoice_number,
      client: inv.client_name,
      currentDate: inv.due_date ? inv.due_date.split('T')[0] : '',
      newDate: inv.due_date ? inv.due_date.split('T')[0] : '',
      updateAsaas: !!inv.asaas_charge_id,
      hasAsaas: !!inv.asaas_charge_id,
    })));
    setBulkDueDateModal(true);
  };

  const handleSaveBulkDueDate = async () => {
    setSavingBulkDueDate(true);
    const toastId = toast.loading('Salvando vencimentos...');
    let ok = 0;
    for (const row of bulkDueDateRows) {
      if (!row.newDate) continue;
      try {
        await apiService.updateInvoiceDueDate(row.id, row.newDate, row.updateAsaas);
        ok++;
      } catch {}
    }
    toast.success(`${ok} vencimento(s) atualizado(s)`, { id: toastId });
    setSavingBulkDueDate(false);
    setBulkDueDateModal(false);
    setSelectedIds(new Set());
    fetchInvoices();
  };

  const statusLabel = (status) => {
    const map = { PENDING: 'Pendente', PAID: 'Paga', OVERDUE: 'Vencida', CANCELLED: 'Cancelada' };
    return map[(status || '').toUpperCase()] || status || '';
  };

  const openEditModal = async (invoice) => {
    setOpenActionMenu(null);
    const toastId = toast.loading('Carregando dados da fatura...');
    try {
      const res = await apiService.getInvoice(invoice.id);
      const inv = res?.data || res;
      let accounts = [];
      try { const r = await apiService.getBankAccounts({ status: 'active' }); accounts = r.data || r || []; } catch {}
      const items = (inv.items || []).map(it => ({ id: it.id, item_number: it.item_number, description: it.description || '' }));
      const form = {
        total_value: parseFloat(inv.total_value).toFixed(2).replace('.', ','),
        due_date: inv.due_date ? inv.due_date.split('T')[0] : '',
        period_start: inv.period_start ? inv.period_start.split('T')[0] : '',
        period_end: inv.period_end ? inv.period_end.split('T')[0] : '',
        charge_date: inv.charge_date ? inv.charge_date.split('T')[0] : '',
        notes: inv.notes || '',
        justification: '',
        mark_paid: false,
        paid_date: new Date().toISOString().split('T')[0],
        paid_account: inv.asaas_charge_id ? 'asaas' : '',
      };
      setEditForm(form);
      setEditItems(items);
      setEditBankAccounts(accounts);
      setEditModal({ invoice: inv, original: { ...form }, items: items.map(i => ({ ...i })) });
      toast.dismiss(toastId);
    } catch {
      toast.error('Erro ao carregar dados da fatura', { id: toastId });
    }
  };

  const handleSaveEdit = async (regenerateAsaas = false) => {
    if (!editModal) return;
    setSavingEdit(true);
    const toastId = toast.loading('Salvando alterações...');
    const inv = editModal.invoice;
    const orig = editModal.original;
    let changed = 0;
    try {
      if (editForm.total_value !== orig.total_value) {
        const value = parseFloat(editForm.total_value.replace(/\./g, '').replace(',', '.'));
        if (!value || value <= 0) { toast.error('Valor inválido', { id: toastId }); setSavingEdit(false); return; }
        await apiService.updateInvoiceValue(inv.id, value, regenerateAsaas, editForm.justification || null);
        changed++;
      }
      if (editForm.due_date !== orig.due_date) {
        await apiService.updateInvoiceDueDate(inv.id, editForm.due_date, regenerateAsaas);
        changed++;
      }
      if (editForm.period_start !== orig.period_start || editForm.period_end !== orig.period_end) {
        if (!editForm.period_start || !editForm.period_end) { toast.error('Informe início e fim do período', { id: toastId }); setSavingEdit(false); return; }
        await apiService.updateInvoicePeriod(inv.id, editForm.period_start, editForm.period_end);
        changed++;
      }
      if (editForm.charge_date !== orig.charge_date) {
        await apiService.updateInvoiceChargeDate(inv.id, editForm.charge_date || null);
        changed++;
      }
      const itemsChanged = editItems.some((it, i) => it.description !== editModal.items[i]?.description);
      const notesChanged = editForm.notes !== orig.notes;
      if (itemsChanged || notesChanged) {
        for (const item of editItems) {
          const origItem = editModal.items.find(i => i.id === item.id);
          if (origItem && item.description !== origItem.description) {
            await apiService.request(`/invoices/items/${item.id}/description`, { method: 'PUT', data: { description: item.description } });
          }
        }
        await apiService.regenerateInvoicePDF(inv.id, editForm.notes);
        changed++;
      }
      if (editForm.mark_paid) {
        if (!editForm.paid_date) { toast.error('Informe a data de pagamento', { id: toastId }); setSavingEdit(false); return; }
        if (!editForm.paid_account) { toast.error('Selecione a conta de recebimento', { id: toastId }); setSavingEdit(false); return; }
        let bankAccountId = null, accountLabel = null;
        if (editForm.paid_account === 'asaas') { accountLabel = 'Asaas'; }
        else { const acc = editBankAccounts.find(a => String(a.id) === String(editForm.paid_account)); bankAccountId = acc?.id || null; accountLabel = acc ? (acc.name || acc.bank_name) : null; }
        await apiService.markInvoiceAsPaid(inv.id, editForm.paid_date, bankAccountId, accountLabel);
        changed++;
      }
      toast.success(changed > 0 ? `${changed} alteração(ões) salva(s)!` : 'Nenhuma alteração detectada', { id: toastId });
      setEditModal(null);
      loadInvoices();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao salvar', { id: toastId });
    } finally {
      setSavingEdit(false);
    }
  };

  const openCancelModal = (invoice) => {
    setOpenActionMenu(null);
    setCancelModal({ id: invoice.id, invoice_number: invoice.invoice_number, client_name: invoice.client_name, asaas_charge_id: invoice.asaas_charge_id });
    setCancelJustification('');
  };

  const handleCancelInvoice = async () => {
    setCancellingInvoice(true);
    try {
      await apiService.cancelInvoice(cancelModal.id, cancelJustification || 'Cancelada manualmente', cancelJustification || null);
      toast.success('Fatura cancelada com sucesso');
      setCancelModal(null);
      setCancelJustification('');
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao cancelar fatura');
    } finally {
      setCancellingInvoice(false);
    }
  };

  const openHistoryModal = async (invoice) => {
    setOpenActionMenu(null);
    setHistoryModal({ id: invoice.id, invoice_number: invoice.invoice_number, client_name: invoice.client_name });
    setHistoryLogs([]);
    setLoadingHistory(true);
    try {
      const res = await apiService.getInvoiceLogs(invoice.id);
      setHistoryLogs(res.data || []);
    } catch {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoadingHistory(false);
    }
  };

  const exportExcel = async () => {
    if (exportTarget.length === 0) return toast.error('Nenhuma fatura para exportar');
    setExportingExcel(true);
    try {
      const wb = new ExcelJS.Workbook();
      wb.creator = 'VIPLOC System';
      wb.created = new Date();
      const ws = wb.addWorksheet('Faturas', { views: [{ state: 'frozen', ySplit: 3 }] });

      // Título
      ws.mergeCells('A1:I1');
      const titleCell = ws.getCell('A1');
      titleCell.value = 'VIPLOC — Relatório de Faturas';
      titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1D4ED8' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      ws.getRow(1).height = 36;

      // Subtítulo
      ws.mergeCells('A2:I2');
      const sub = ws.getCell('A2');
      sub.value = `Gerado em ${new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} | ${exportTarget.length} fatura(s)`;
      sub.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF6B7280' } };
      sub.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } };
      sub.alignment = { horizontal: 'center', vertical: 'middle' };
      ws.getRow(2).height = 20;

      // Cabeçalhos
      const headers = ['Nº Fatura', 'Cliente', 'Veículo', 'Placa', 'Período', 'Vencimento', 'Valor (R$)', 'Status', 'Parcelas'];
      const headerRow = ws.addRow(headers);
      headerRow.height = 26;
      headerRow.eachCell((cell) => {
        cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF374151' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: false };
        cell.border = { bottom: { style: 'thin', color: { argb: 'FF6B7280' } } };
      });

      // Cores por status
      const statusColors = {
        PAID: { bg: 'FFD1FAE5', fg: 'FF065F46' },
        PENDING: { bg: 'FFFEF3C7', fg: 'FF92400E' },
        OVERDUE: { bg: 'FFFEE2E2', fg: 'FF991B1B' },
        CANCELLED: { bg: 'FFF3F4F6', fg: 'FF6B7280' },
      };

      exportTarget.forEach((inv, idx) => {
        const period = inv.period_start
          ? `${formatDate(inv.period_start)} a ${formatDate(inv.period_end)}`
          : '';
        const parcelas = inv.contract_expected_months
          ? `${inv.contract_paid_invoices || 0}/${inv.contract_expected_months}`
          : '';
        const row = ws.addRow([
          `#${inv.invoice_number}`,
          inv.client_name || '',
          `${inv.vehicle_type || ''} ${inv.vehicle_brand || ''} ${inv.vehicle_model || ''}`.trim(),
          inv.vehicle_plate || '',
          period,
          inv.due_date ? formatDate(inv.due_date) : '',
          parseFloat(inv.total_value || 0),
          statusLabel(inv.status),
          parcelas,
        ]);
        row.height = 22;

        const isEven = idx % 2 === 0;
        const sc = statusColors[(inv.status || '').toUpperCase()] || statusColors.CANCELLED;

        row.eachCell((cell, colNum) => {
          cell.font = { name: 'Calibri', size: 10 };
          cell.alignment = { vertical: 'middle', horizontal: colNum === 7 ? 'right' : colNum === 1 || colNum === 9 ? 'center' : 'left' };
          cell.border = {
            bottom: { style: 'hair', color: { argb: 'FFE5E7EB' } },
          };
          if (!isEven) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
        });

        // Status colorido
        const statusCell = row.getCell(8);
        statusCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: sc.bg } };
        statusCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: sc.fg } };
        statusCell.alignment = { horizontal: 'center', vertical: 'middle' };

        // Valor como número
        const valCell = row.getCell(7);
        valCell.numFmt = 'R$ #,##0.00';
      });

      // Linha de totais
      const totalRow = ws.addRow([
        '', '', '', '', '', 'TOTAL',
        exportTarget.reduce((s, i) => s + parseFloat(i.total_value || 0), 0),
        '', ''
      ]);
      totalRow.height = 24;
      totalRow.eachCell((cell, colNum) => {
        cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1D4ED8' } };
        cell.alignment = { vertical: 'middle', horizontal: colNum === 7 ? 'right' : colNum === 6 ? 'right' : 'center' };
      });
      const totalValCell = totalRow.getCell(7);
      totalValCell.numFmt = 'R$ #,##0.00';

      // Larguras das colunas
      ws.columns = [
        { width: 12 }, { width: 32 }, { width: 24 }, { width: 12 },
        { width: 26 }, { width: 14 }, { width: 16 }, { width: 14 }, { width: 10 }
      ];

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `faturas-viploc-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Excel gerado: ${exportTarget.length} faturas`);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao gerar Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const exportPDF = () => {
    if (exportTarget.length === 0) return toast.error('Nenhuma fatura para exportar');
    setExportingPDF(true);
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();

      // Header
      doc.setFillColor(29, 78, 216);
      doc.rect(0, 0, pageW, 22, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('VIPLOC — Relatório de Faturas', pageW / 2, 13, { align: 'center' });

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Gerado em ${new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}  |  ${exportTarget.length} fatura(s)`,
        pageW / 2, 19, { align: 'center' }
      );

      const total = exportTarget.reduce((s, i) => s + parseFloat(i.total_value || 0), 0);

      autoTable(doc, {
        startY: 26,
        head: [['Nº', 'Cliente', 'Veículo / Placa', 'Período', 'Vencimento', 'Valor', 'Status', 'Parcelas']],
        body: exportTarget.map(inv => [
          `#${inv.invoice_number}`,
          inv.client_name || '',
          `${inv.vehicle_type || ''} ${inv.vehicle_brand || ''} - ${inv.vehicle_plate || ''}`.trim(),
          inv.period_start ? `${formatDate(inv.period_start)}\na ${formatDate(inv.period_end)}` : '',
          formatDate(inv.due_date),
          formatCurrency(inv.total_value),
          statusLabel(inv.status),
          inv.contract_expected_months ? `${inv.contract_paid_invoices || 0}/${inv.contract_expected_months}` : '-',
        ]),
        foot: [['', '', '', '', 'TOTAL', formatCurrency(total), '', '']],
        headStyles: { fillColor: [55, 65, 81], textColor: 255, fontSize: 9, fontStyle: 'bold', halign: 'center' },
        footStyles: { fillColor: [29, 78, 216], textColor: 255, fontSize: 9, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        bodyStyles: { fontSize: 8, textColor: [17, 24, 39] },
        columnStyles: {
          0: { halign: 'center', cellWidth: 16 },
          1: { cellWidth: 62 },
          2: { cellWidth: 54 },
          3: { cellWidth: 40 },
          4: { halign: 'center', cellWidth: 28 },
          5: { halign: 'right', cellWidth: 33, fontStyle: 'bold' },
          6: { halign: 'center', cellWidth: 28 },
          7: { halign: 'center', cellWidth: 16 },
        },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.index === 6) {
            const st = (exportTarget[data.row.index]?.status || '').toUpperCase();
            const colors = { PAID: [209, 250, 229], PENDING: [254, 243, 199], OVERDUE: [254, 226, 226], CANCELLED: [243, 244, 246] };
            const textColors = { PAID: [6, 95, 70], PENDING: [146, 64, 14], OVERDUE: [153, 27, 27], CANCELLED: [107, 114, 128] };
            const bg = colors[st] || colors.CANCELLED;
            const fg = textColors[st] || textColors.CANCELLED;
            doc.setFillColor(...bg);
            doc.roundedRect(data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2, 2, 2, 'F');
            doc.setTextColor(...fg);
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.text(statusLabel(exportTarget[data.row.index]?.status), data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 1, { align: 'center' });
          }
        },
        margin: { left: 10, right: 10 },
        tableLineColor: [229, 231, 235],
        tableLineWidth: 0.1,
      });

      // Rodapé de páginas
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(156, 163, 175);
        doc.setFont('helvetica', 'normal');
        doc.text(`VIPLOC System — Confidencial`, 10, doc.internal.pageSize.getHeight() - 5);
        doc.text(`Página ${i} de ${pageCount}`, pageW - 10, doc.internal.pageSize.getHeight() - 5, { align: 'right' });
      }

      doc.save(`faturas-viploc-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(`PDF gerado: ${exportTarget.length} faturas`);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao gerar PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const openExtrajudicialModal = async (invoice) => {
    setExtrajudicialModal(invoice);
    setExtrajudicialData(null);
    setLoadingExtrajudicial(true);
    try {
      const res = await api.get(`/api/invoices/${invoice.id}/extrajudicial-data`);
      setExtrajudicialData(res.data.data);
    } catch (err) {
      toast.error('Erro ao carregar dados da notificação');
      setExtrajudicialModal(null);
    } finally {
      setLoadingExtrajudicial(false);
    }
  };

  const buildExtrajudicialPdf = (data) => {
    const { invoice, companyPartner } = data;

    let customData = {};
    try { customData = JSON.parse(invoice.custom_data || '{}'); } catch (e) {}

    const representativeName = customData.locatario_representante_nome || '';
    const representativeCpf = customData.locatario_representante_cpf || '';

    const contractYear = invoice.contract_start_date
      ? new Date(invoice.contract_start_date).getFullYear()
      : new Date().getFullYear();
    const contractNum = invoice.contract_number
      ? `${invoice.contract_number}/${contractYear}`
      : '—';

    const totalValue = parseFloat(invoice.total_value) || 0;
    const dueDate = new Date(invoice.due_date);
    const today = new Date();
    const daysOverdue = Math.max(0, Math.floor((today - dueDate) / (1000 * 60 * 60 * 24)));
    const multa = totalValue * 0.10;
    const juros = totalValue * 0.01 * (daysOverdue / 30);
    const totalDue = totalValue + multa + juros;

    const fmtCurr = (v) => {
      return 'R$ ' + v.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };
    const fmtDate = (d) => {
      if (!d) return '—';
      const dt = new Date(d);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
    };
    const numToWords = (n) => {
      const u = ['','um','dois','três','quatro','cinco','seis','sete','oito','nove',
        'dez','onze','doze','treze','quatorze','quinze','dezesseis','dezessete','dezoito','dezenove'];
      const t = ['','','vinte','trinta','quarenta','cinquenta','sessenta','setenta','oitenta','noventa'];
      if (n === 0) return 'zero';
      if (n < 20) return u[n];
      if (n % 10 === 0) return t[Math.floor(n/10)];
      return `${t[Math.floor(n/10)]} e ${u[n%10]}`;
    };

    const todayStr = fmtDate(today);
    const dueDateStr = fmtDate(dueDate);
    const companyName = 'ANDRADES NEGOCIOS & SOLUCOES COMERCIAIS LTDA';
    const companyCnpj = '63.786.933/0001-76';
    const companyAddr = 'Av das Bandeiras 2035, Jardim Joquei Club, Campo Grande - MS, CEP 79081-310';

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = 210;
    const pageH = 297;
    const mL = 20; const mR = 20;
    const mT = 20;
    const usableW = pageW - mL - mR;
    let y = mT;

    const checkPB = (need = 10) => {
      if (y + need > pageH - 18) { doc.addPage(); y = mT; }
    };

    // Header bar
    doc.setFillColor(0, 51, 102);
    doc.rect(0, 0, pageW, 28, 'F');
    doc.setTextColor(255,255,255);
    doc.setFontSize(16); doc.setFont('helvetica','bold');
    doc.text('VIPLOC', mL, 10);
    doc.setFontSize(8); doc.setFont('helvetica','normal');
    doc.text('Locação de Veículos — viploclocadora.com.br', mL, 16);
    doc.text('(67) 99868-4424 | CNPJ: 63.786.933/0001-76', mL, 21);

    y = 38;

    // Title
    doc.setTextColor(0,0,0);
    doc.setFontSize(14); doc.setFont('helvetica','bold');
    doc.text('NOTIFICAÇÃO EXTRAJUDICIAL', pageW/2, y, { align:'center' });
    y += 6;
    doc.setFontSize(8); doc.setFont('helvetica','normal');
    doc.setTextColor(80,80,80);
    doc.text(`Contrato nº ${contractNum}  |  Fatura nº ${invoice.invoice_number || invoice.id}  |  Campo Grande - MS, ${todayStr}`, pageW/2, y, { align:'center' });
    y += 8;

    doc.setDrawColor(0,51,102); doc.setLineWidth(0.6);
    doc.line(mL, y, pageW - mR, y);
    y += 7;

    // Intro
    doc.setFontSize(10); doc.setFont('helvetica','normal'); doc.setTextColor(0,0,0);
    const introTxt = 'Pelo presente instrumento particular, vem a Notificante NOTIFICAR e INTERPELAR o Notificado, pelos fatos e fundamentos a seguir expostos:';
    const introLines = doc.splitTextToSize(introTxt, usableW);
    doc.text(introLines, mL, y);
    y += introLines.length * 5 + 6;

    // Section helper
    const section = (title) => {
      checkPB(20);
      doc.setFontSize(10); doc.setFont('helvetica','bold'); doc.setTextColor(0,51,102);
      doc.text(title, mL, y);
      y += 6;
      doc.setFont('helvetica','normal'); doc.setTextColor(0,0,0);
    };

    const textBlock = (txt, indent = 0) => {
      const lines = doc.splitTextToSize(txt, usableW - indent);
      doc.setFontSize(10);
      doc.text(lines, mL + indent, y);
      y += lines.length * 5 + 2;
    };

    // 1. Notificante
    section('1. NOTIFICANTE (CREDORA)');
    textBlock(`Razão Social: ${companyName}`);
    textBlock(`CNPJ: ${companyCnpj}`);
    textBlock(`Endereço: ${companyAddr}`);
    if (companyPartner) textBlock(`Representante Legal: ${companyPartner.name}${companyPartner.cpf ? ' — CPF: ' + companyPartner.cpf : ''}`);
    y += 3;

    // 2. Notificado
    section('2. NOTIFICADO (DEVEDOR)');
    textBlock(`Razão Social/Nome: ${invoice.client_name || '—'}`);
    textBlock(`CPF/CNPJ: ${invoice.client_cpf_cnpj || '—'}`);
    const clientAddr = [invoice.client_address, invoice.client_city, invoice.client_state, invoice.client_zip_code].filter(Boolean).join(', ');
    if (clientAddr) textBlock(`Endereço: ${clientAddr}`);
    if (representativeName) textBlock(`Representante Legal: ${representativeName}${representativeCpf ? ' — CPF: ' + representativeCpf : ''}`);
    y += 3;

    // 3. Relação Contratual
    section('3. DA RELAÇÃO CONTRATUAL');
    textBlock(`As partes celebraram Contrato de Locação de Veículo nº ${contractNum}, mediante o qual a Notificante cedeu ao Notificado o uso do seguinte veículo:`);
    y += 2;

    const vehicleRows = [
      ['Marca/Modelo', `${invoice.brand || ''} ${invoice.model || ''}`.trim() || '—'],
      ['Placa', invoice.license_plate || '—'],
      ['Chassi', invoice.chassis || '—'],
      ['Ano', String(invoice.vehicle_year || '—')],
    ];
    autoTable(doc, {
      startY: y,
      body: vehicleRows,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: [1,2] },
      columnStyles: {
        0: { fontStyle:'bold', cellWidth:35, textColor:[60,60,60] },
        1: { cellWidth:135 }
      },
      margin: { left: mL + 4, right: mR }
    });
    y = doc.lastAutoTable.finalY + 6;

    // 4. Inadimplência
    section('4. DA INADIMPLÊNCIA');
    textBlock(`O Notificado encontra-se em débito com a Notificante referente à FATURA Nº ${invoice.invoice_number || invoice.id}, com vencimento em ${dueDateStr}, há ${daysOverdue} (${numToWords(daysOverdue)}) dias corridos, no valor principal de ${fmtCurr(totalValue)}, conforme demonstrativo financeiro abaixo:`);
    y += 2;

    autoTable(doc, {
      startY: y,
      head: [['Descrição', 'Valor']],
      body: [
        ['Valor Principal da Fatura', fmtCurr(totalValue)],
        ['Multa Contratual (10%)', fmtCurr(multa)],
        [`Juros de Mora (1% a.m. — ${daysOverdue} dias)`, fmtCurr(juros)],
        ['TOTAL DEVIDO', fmtCurr(totalDue)],
      ],
      theme: 'striped',
      headStyles: { fillColor:[0,51,102], textColor:[255,255,255], fontSize:9, fontStyle:'bold' },
      bodyStyles: { fontSize:9 },
      columnStyles: { 0:{ cellWidth:120 }, 1:{ cellWidth:50, halign:'right' } },
      didParseCell: (data) => {
        if (data.row.index === 3) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [232,241,255];
          data.cell.styles.textColor = [0,51,102];
        }
      },
      margin: { left: mL, right: mR }
    });
    y = doc.lastAutoTable.finalY + 6;

    // 5. Interpelação
    section('5. DA INTERPELAÇÃO');
    textBlock(`Pelo presente instrumento, fica o Notificado INTERPELADO a efetuar o pagamento da importância total de ${fmtCurr(totalDue)}, no prazo de 5 (cinco) dias úteis contados do recebimento desta notificação, mediante depósito ou transferência para a seguinte conta bancária:`);
    y += 3;

    // Bank info box
    checkPB(28);
    doc.setFillColor(240,246,255);
    doc.setDrawColor(0,51,102); doc.setLineWidth(0.4);
    doc.rect(mL, y-2, usableW, 27, 'FD');
    doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(0,51,102);
    doc.text('DADOS BANCÁRIOS PARA PAGAMENTO', mL+4, y+3);
    doc.setFont('helvetica','normal'); doc.setTextColor(0,0,0);
    doc.text('Banco: Sicredi — Cooperativa de Crédito', mL+4, y+9);
    doc.text('Agência: 0162 — Conta Corrente: 115.547-8', mL+4, y+15);
    doc.text(`Favorecido: ${companyName}`, mL+4, y+21);
    doc.text(`CNPJ: ${companyCnpj}`, mL+4, y+26);
    y += 33;

    // 6. Consequências
    section('6. DAS CONSEQUÊNCIAS');
    textBlock('O não pagamento no prazo estipulado implicará na adoção das seguintes medidas, independentemente de nova notificação:');
    y += 1;
    const conseq = [
      'a) Rescisão imediata do Contrato de Locação;',
      'b) Reintegração do veículo locado;',
      'c) Ajuizamento de Ação de Cobrança pelos valores em atraso, acrescidos de honorários advocatícios;',
      'd) Inclusão do nome do Notificado nos cadastros de proteção ao crédito (SPC/SERASA).'
    ];
    for (const c of conseq) { checkPB(8); textBlock(c, 4); }
    y += 4;

    // Closing
    checkPB(20);
    textBlock('Por ser medida de Direito e Justiça, esperamos o pronto atendimento desta notificação.');
    y += 5;
    doc.setFont('helvetica','normal'); doc.setFontSize(10); doc.setTextColor(0,0,0);
    doc.text(`Campo Grande - MS, ${todayStr}.`, mL, y);
    y += 18;

    // Signature
    checkPB(25);
    doc.setDrawColor(0,0,0); doc.setLineWidth(0.3);
    doc.line(mL, y, mL+85, y);
    y += 5;
    doc.setFont('helvetica','bold'); doc.setFontSize(10);
    doc.text(companyPartner ? companyPartner.name : companyName, mL, y);
    y += 5;
    doc.setFont('helvetica','normal'); doc.setFontSize(9);
    if (companyPartner?.cpf) { doc.text(`CPF: ${companyPartner.cpf}`, mL, y); y += 4; }
    doc.text('Representante Legal — VIPLOC', mL, y); y += 4;
    doc.text(companyName, mL, y);

    return { doc, totalDue, daysOverdue };
  };

  const openExtrajudicialStatusModal = async (invoice) => {
    setExtrajudicialStatusModal({ invoice, loading: true, data: null });
    try {
      const res = await api.get(`/api/invoices/${invoice.id}/extrajudicial-status`);
      setExtrajudicialStatusModal({ invoice, loading: false, data: res.data.data });
    } catch (err) {
      toast.error('Erro ao verificar status');
      setExtrajudicialStatusModal(null);
    }
  };

  const handleSendExtrajudicial = async () => {
    if (!extrajudicialData) return;
    setSendingExtrajudicial(true);
    try {
      const { doc } = buildExtrajudicialPdf(extrajudicialData);
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const res = await api.post(
        `/api/invoices/${extrajudicialModal.id}/send-extrajudicial-notification`,
        { pdfBase64 }
      );
      toast.success(res.data.message || 'Notificação enviada ao ClickSign com sucesso!');
      setExtrajudicialModal(null);
      setExtrajudicialData(null);
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao enviar notificação';
      toast.error(msg);
    } finally {
      setSendingExtrajudicial(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            📄 Faturas
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gerenciar faturas de locação
          </p>
        </div>
        <div className="flex gap-3 flex-wrap justify-end">
          <button
            onClick={exportExcel}
            disabled={exportingExcel || exportTarget.length === 0}
            className={`relative px-4 py-2 rounded-lg transition font-semibold text-sm flex items-center gap-2 ${
              exportTarget.length > 0
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            {exportingExcel ? (
              <span className="animate-spin">⏳</span>
            ) : '📊'}
            Excel
            {selectedIds.size > 0 && (
              <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {selectedIds.size}
              </span>
            )}
          </button>
          <button
            onClick={exportPDF}
            disabled={exportingPDF || exportTarget.length === 0}
            className={`relative px-4 py-2 rounded-lg transition font-semibold text-sm flex items-center gap-2 ${
              exportTarget.length > 0
                ? 'bg-orange-600 text-white hover:bg-orange-700'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            {exportingPDF ? (
              <span className="animate-spin">⏳</span>
            ) : '📋'}
            PDF
            {selectedIds.size > 0 && (
              <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {selectedIds.size}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
          >
            🔄 Gerar Faturas Mensais
          </button>
          <button
            onClick={() => navigate('/invoices/new')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
          >
            + Nova Fatura
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        <StatCard title="Total Faturas" value={statistics.total} icon="📊" color="blue" />
        <StatCard title="Pendentes" value={statistics.pending} subtitle={formatCurrency(statistics.total_pending)} icon="⏳" color="yellow" />
        <StatCard title="Pagas" value={statistics.paid} subtitle={formatCurrency(statistics.total_received)} icon="✅" color="green" />
        <StatCard title="Vencidas" value={statistics.overdue} subtitle={formatCurrency(statistics.total_overdue)} icon="🔴" color="red" />
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Status */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-32 flex-shrink-0"
          >
            <option value="">Status</option>
            <option value="PENDING">Pendente</option>
            <option value="PAID">Paga</option>
            <option value="OVERDUE">Vencida</option>
            <option value="CANCELLED">Cancelada</option>
          </select>

          {/* Cliente */}
          <input
            type="text"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            placeholder="Cliente..."
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 w-44 flex-shrink-0"
          />

          {/* Placa */}
          <input
            type="text"
            value={plateFilter}
            onChange={(e) => setPlateFilter(e.target.value.toUpperCase())}
            placeholder="Placa..."
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 w-28 flex-shrink-0"
          />

          {/* Vencimento */}
          <div className="flex items-center gap-1 flex-shrink-0 w-52">
            <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">Venc.</span>
            <div className="flex-1"><DateRangePicker value={dateRange} onChange={setDateRange} /></div>
          </div>

          {/* Dt. Pagamento */}
          <div className="flex items-center gap-1 flex-shrink-0 w-56">
            <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">Pagto.</span>
            <div className="flex-1"><DateRangePicker value={paymentDateRange} onChange={setPaymentDateRange} /></div>
          </div>

          {/* Limpar */}
          <button
            onClick={clearFilters}
            className={`px-3 py-2 rounded-lg transition font-medium text-sm whitespace-nowrap flex-shrink-0 ${
              hasActiveFilters
                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 border border-red-300 dark:border-red-700'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600'
            }`}
          >
            {hasActiveFilters ? '✕ Limpar' : '✕'}
          </button>
        </div>

        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {filteredAndSorted.length !== invoices.length
              ? `Exibindo ${filteredAndSorted.length} de ${invoices.length} faturas`
              : `${invoices.length} faturas no total`}
          </p>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                {selectedIds.size} selecionada{selectedIds.size !== 1 ? 's' : ''}
              </span>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
              >
                Limpar seleção
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredAndSorted.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              Nenhuma fatura encontrada
            </p>
          </div>
        ) : (
          <>
          {/* ── Barra de ações em lote ── */}
          {selectedIds.size > 0 && (
            <div className="mx-4 mb-3 flex flex-wrap items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg px-4 py-2 text-sm">
              <span className="font-semibold text-blue-700 dark:text-blue-300 mr-1">{selectedIds.size} selecionada(s)</span>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 text-xs"
              >✕ Desmarcar</button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 text-xs font-semibold"
              >🗑 Excluir selecionadas</button>
              <button
                onClick={openBulkDueDateModal}
                className="px-3 py-1 rounded bg-orange-500 text-white hover:bg-orange-600 text-xs font-semibold"
              >📅 Alterar vencimentos</button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="pl-4 pr-2 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                    />
                  </th>
                  <SortTh col="invoice_number" label="Fatura" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortTh col="client_name" label="Cliente" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortTh col="vehicle_plate" label="Veículo" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Cobrar
                  </th>
                  <SortTh col="period_start" label="Período" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortTh col="due_date" label="Vencimento" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortTh col="total_value" label="Valor" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortTh col="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortTh col="payment_date" label="Dt. Pagto" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Conta
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Pagas
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredAndSorted.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer transition-colors ${selectedIds.has(invoice.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                  >
                    <td className="pl-4 pr-2 py-4 w-10" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(invoice.id)}
                        onChange={() => toggleSelect(invoice.id)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            #{invoice.invoice_number}
                          </div>
                          <div className="text-xs text-gray-500">
                            Emitida: {formatDate(invoice.issue_date)}
                          </div>
                        </div>
                        {!invoice.send_to_client && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            Uso Interno
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {invoice.client_name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {invoice.vehicle_type ? (
                        <div className="text-sm text-gray-900 dark:text-white">
                          <div className="font-medium">{invoice.vehicle_type} {invoice.vehicle_brand}</div>
                          <div className="text-xs text-gray-500">{invoice.vehicle_model} - {invoice.vehicle_plate}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Sem veículo</span>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      {invoice.charge_date ? (
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(invoice.charge_date)}
                          {invoice.send_charge_email && (
                            <span className="ml-1 text-blue-500" title="Envio automático ativo">📧</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white leading-tight">
                        <div>{formatDate(invoice.period_start)}</div>
                        <div className="text-xs text-gray-400">a {formatDate(invoice.period_end)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => editingDueDate?.invoiceId === invoice.id && e.stopPropagation()}>
                      {editingDueDate?.invoiceId === invoice.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            value={newDueDateValue}
                            onChange={(e) => setNewDueDateValue(e.target.value)}
                            className="px-2 py-1 border border-blue-400 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const hasCharge = !!invoice.asaas_charge_id;
                                const regenerate = hasCharge ? window.confirm('Esta fatura tem cobrança no Asaas.\n\nDeseja CANCELAR a cobrança atual e GERAR UMA NOVA com o novo vencimento?') : false;
                                await apiService.updateInvoiceDueDate(invoice.id, newDueDateValue, regenerate);
                                toast.success('Vencimento atualizado!');
                                setEditingDueDate(null);
                                loadInvoices();
                              } catch (error) {
                                toast.error('Erro: ' + (error.response?.data?.message || error.message));
                              }
                            }}
                            className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                          >
                            OK
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingDueDate(null); }}
                            className="px-2 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded text-xs hover:bg-gray-400"
                          >
                            X
                          </button>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(invoice.due_date)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900 dark:text-white">
                        {formatCurrency(invoice.total_value)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {getStatusBadge(invoice.status)}
                        {invoice.extrajudicial_sent_at && (() => {
                          const st = (invoice.extrajudicial_clicksign_status || 'PENDING').toUpperCase();
                          const signed = st === 'SIGNED';
                          return (
                            <span
                              title={`Notificação extrajudicial ${signed ? 'assinada' : 'aguardando assinatura'}`}
                              className={`text-xs px-1.5 py-0.5 rounded font-bold cursor-default ${signed ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}
                            >
                              ⚖️{signed ? '✓' : '…'}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invoice.payment_date ? (
                        <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                          {formatDate(invoice.payment_date)}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      {invoice.payment_account_label ? (
                        <span className="text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                          {invoice.payment_account_label === 'Asaas' ? '💳 ' : '🏦 '}{invoice.payment_account_label}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                    {/* Parcelas pagas x/y */}
                    <td className="px-3 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                      {invoice.contract_expected_months ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-medium tabular-nums">
                          <span className={parseInt(invoice.contract_paid_invoices) > 0 ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : ''}>
                            {invoice.contract_paid_invoices || 0}
                          </span>
                          <span className="text-gray-300 dark:text-gray-600">/</span>
                          {invoice.contract_expected_months}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    {/* Ações dropdown */}
                    <td className="px-4 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const menuHeight = 320; // estimativa máxima
                          const spaceBelow = window.innerHeight - rect.bottom;
                          const top = spaceBelow >= menuHeight
                            ? rect.bottom + 4
                            : rect.top - menuHeight - 4;
                          setMenuPosition({ top: Math.max(8, top), right: window.innerWidth - rect.right });
                          setOpenActionMenu(openActionMenu === invoice.id ? null : invoice.id);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Ações"
                      >
                        ⋮
                      </button>
                      {openActionMenu === invoice.id && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setOpenActionMenu(null)} />
                          <div className="fixed w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1 text-left"
                            style={{ top: menuPosition.top, right: menuPosition.right }}>
                            {(() => { const st = (invoice.status || '').toUpperCase(); const isPending = st === 'PENDING'; const isOverdue = st === 'OVERDUE'; const isActive = isPending || isOverdue; const isDone = st === 'PAID' || st === 'CANCELLED'; return (<>
                            {isPending && (
                              <button
                                onClick={() => { setOpenActionMenu(null); handleQuickAction(invoice.id, 'finalize'); }}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                ⚡ {invoice.asaas_charge_id ? 'Regerar Boleto/PIX' : 'Gerar Boleto/PIX'}
                              </button>
                            )}
                            {!invoice.pdf_url ? (
                              <button
                                onClick={() => { setOpenActionMenu(null); handleQuickAction(invoice.id, 'generate-pdf'); }}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                📝 Gerar PDF
                              </button>
                            ) : (
                              <a
                                href={invoice.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setOpenActionMenu(null)}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                📄 Ver PDF
                              </a>
                            )}
                            {isPending && (
                              <button
                                onClick={() => { setOpenActionMenu(null); handleQuickAction(invoice.id, 'email'); }}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                📧 Enviar por Email
                              </button>
                            )}
                            {isPending && (
                              <button
                                onClick={() => { setOpenActionMenu(null); handleQuickAction(invoice.id, 'whatsapp'); }}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                💬 Enviar por WhatsApp
                              </button>
                            )}
                            {!isDone && (
                              <button
                                onClick={() => openEditModal(invoice)}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-blue-600 font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20"
                              >
                                ✏️ Editar Fatura
                              </button>
                            )}
                            {invoice.asaas_charge_id && isActive && (
                              <button
                                onClick={() => { setOpenActionMenu(null); handleQuickAction(invoice.id, 'cancel-asaas', invoice.asaas_charge_id); }}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                              >
                                ❌ Cancelar Boleto Asaas
                              </button>
                            )}
                            {(() => {
                              const alreadySent = !!invoice.extrajudicial_sent_at;
                              if (alreadySent) return (
                                <button
                                  onClick={() => { setOpenActionMenu(null); openExtrajudicialStatusModal(invoice); }}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 font-semibold"
                                >
                                  ⚖️ Ver Status Notificação Extrajudicial
                                </button>
                              );
                              if (!isOverdue) return null;
                              const daysOv = invoice.due_date
                                ? Math.floor((new Date() - new Date(invoice.due_date)) / (1000*60*60*24))
                                : 0;
                              return daysOv >= 10 ? (
                                <button
                                  onClick={() => { setOpenActionMenu(null); openExtrajudicialModal(invoice); }}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-semibold"
                                >
                                  ⚖️ Notificação Extrajudicial
                                </button>
                              ) : null;
                            })()}
                            <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                            <button
                              onClick={() => openHistoryModal(invoice)}
                              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              🕐 Ver Histórico
                            </button>
                            {!isDone && (
                              <button
                                onClick={() => openCancelModal(invoice)}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                🗑️ Excluir Fatura
                              </button>
                            )}
                            </>); })()}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Modal Unificado — Editar Fatura */}
      {editModal && (() => {
        const inv = editModal.invoice;
        const st = (inv.status || '').toUpperCase();
        const isActive = st === 'PENDING' || st === 'OVERDUE';
        const hasAsaas = !!inv.asaas_charge_id;
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">✏️ Editar Fatura</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  #{inv.invoice_number} — {inv.client_name}
                </p>
              </div>
              <button onClick={() => setEditModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>

            <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
              {/* Valor */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Valor (R$)</label>
                  <input
                    type="text"
                    value={editForm.total_value}
                    onChange={e => setEditForm(f => ({ ...f, total_value: e.target.value }))}
                    onFocus={e => e.target.select()}
                    className="w-full px-3 py-2 text-lg font-bold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Vencimento</label>
                  <input
                    type="date"
                    value={editForm.due_date}
                    onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Período */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Início do Período</label>
                  <input
                    type="date"
                    value={editForm.period_start}
                    onChange={e => setEditForm(f => ({ ...f, period_start: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Fim do Período</label>
                  <input
                    type="date"
                    value={editForm.period_end}
                    onChange={e => setEditForm(f => ({ ...f, period_end: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Data de Cobrança */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Data de Cobrança <span className="text-xs font-normal text-gray-400">(envio automático do boleto)</span>
                </label>
                <input
                  type="date"
                  value={editForm.charge_date}
                  onChange={e => setEditForm(f => ({ ...f, charge_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              {/* Descrição dos Itens */}
              {editItems.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Descrição dos Itens</label>
                  <div className="space-y-2">
                    {editItems.map((item, idx) => (
                      <div key={item.id} className="flex gap-2 items-start">
                        <span className="text-xs text-gray-400 mt-2.5 w-6 flex-shrink-0">{item.item_number}</span>
                        <textarea
                          value={item.description}
                          onChange={e => setEditItems(prev => prev.map((it, i) => i === idx ? { ...it, description: e.target.value } : it))}
                          rows={2}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Observações */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Observações <span className="text-xs font-normal text-gray-400">(rodapé do PDF)</span>
                </label>
                <textarea
                  value={editForm.notes}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  placeholder="Ex: Referente ao mês de junho/2026..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                />
              </div>

              {/* Justificativa (para alteração de valor) */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Justificativa <span className="text-xs font-normal text-gray-400">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={editForm.justification}
                  onChange={e => setEditForm(f => ({ ...f, justification: e.target.value }))}
                  placeholder="Motivo da alteração..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              {/* Marcar como Paga */}
              {isActive && (
                <div className={`p-4 rounded-lg border-2 transition-colors ${editForm.mark_paid ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'}`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.mark_paid}
                      onChange={e => setEditForm(f => ({ ...f, mark_paid: e.target.checked }))}
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Marcar como Paga</span>
                  </label>
                  {editForm.mark_paid && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Data Pagamento</label>
                        <input
                          type="date"
                          value={editForm.paid_date}
                          onChange={e => setEditForm(f => ({ ...f, paid_date: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-emerald-300 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Conta</label>
                        <select
                          value={editForm.paid_account}
                          onChange={e => setEditForm(f => ({ ...f, paid_account: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-emerald-300 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">Selecione...</option>
                          {hasAsaas && <option value="asaas">Asaas (PIX/Boleto)</option>}
                          {editBankAccounts.map(acc => (
                            <option key={acc.id} value={String(acc.id)}>
                              {acc.name || acc.bank_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Aviso Asaas */}
              {hasAsaas && (
                <p className="text-xs text-orange-600 bg-orange-50 dark:bg-orange-900/20 rounded-lg px-3 py-2">
                  Esta fatura possui cobrança no Asaas. Ao alterar valor ou vencimento, escolha se deseja regerar a cobranca.
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2 flex-shrink-0">
              {hasAsaas ? (
                <>
                  <button
                    onClick={() => handleSaveEdit(true)}
                    disabled={savingEdit}
                    className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    {savingEdit ? 'Salvando...' : 'Salvar + Regerar Cobranca Asaas'}
                  </button>
                  <button
                    onClick={() => handleSaveEdit(false)}
                    disabled={savingEdit}
                    className="w-full py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-semibold text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition"
                  >
                    {savingEdit ? 'Salvando...' : 'Salvar (manter cobranca atual)'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleSaveEdit(false)}
                  disabled={savingEdit}
                  className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {savingEdit ? 'Salvando...' : 'Salvar Alteracoes'}
                </button>
              )}
              <button
                onClick={() => setEditModal(null)}
                disabled={savingEdit}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Modal Cancelar / Excluir Fatura ── */}
      {cancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">🗑️ Excluir Fatura</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  #{cancelModal.invoice_number} — {cancelModal.client_name}
                </p>
              </div>
              <button onClick={() => setCancelModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-400 font-medium">⚠️ Esta ação não pode ser desfeita.</p>
                {cancelModal.asaas_charge_id && (
                  <p className="text-xs text-red-600 dark:text-red-500 mt-1">O boleto/PIX no Asaas também será cancelado.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Motivo / Justificativa <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelJustification}
                  onChange={e => setCancelJustification(e.target.value)}
                  rows={3}
                  placeholder="Descreva o motivo do cancelamento..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:outline-none resize-none"
                  autoFocus
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={() => setCancelModal(null)}
                disabled={cancellingInvoice}
                className="flex-1 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCancelInvoice}
                disabled={cancellingInvoice || !cancelJustification.trim()}
                className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition"
              >
                {cancellingInvoice ? '⏳ Excluindo...' : '🗑️ Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Histórico da Fatura ── */}
      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">🕐 Histórico da Fatura</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  #{historyModal.invoice_number} — {historyModal.client_name}
                </p>
              </div>
              <button onClick={() => setHistoryModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-4">
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : historyLogs.length === 0 ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-8">Nenhum registro encontrado</p>
              ) : (
                <div className="space-y-3">
                  {historyLogs.map((log, i) => {
                    const actionLabels = {
                      CREATED: { label: 'Fatura criada', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: '📄' },
                      VALUE_CHANGED: { label: 'Valor alterado', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', icon: '💲' },
                      DUE_DATE_CHANGED: { label: 'Vencimento alterado', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: '📅' },
                      STATUS_CHANGED: { label: 'Status alterado', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300', icon: '🔄' },
                      PAID: { label: 'Marcada como paga', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: '✅' },
                      CANCELLED: { label: 'Cancelada', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: '🗑️' },
                      PDF_GENERATED: { label: 'PDF gerado', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: '📄' },
                      ASAAS_INTEGRATED: { label: 'Cobrança Asaas criada', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300', icon: '💳' },
                      EMAIL_SENT: { label: 'Email enviado', color: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300', icon: '📧' },
                    };
                    const meta = actionLabels[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: '📋' };
                    const dt = new Date(log.created_at);
                    const dtStr = dt.toLocaleDateString('pt-BR') + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

                    return (
                      <div key={log.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm flex-shrink-0">
                            {meta.icon}
                          </div>
                          {i < historyLogs.length - 1 && (
                            <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 mt-1" />
                          )}
                        </div>
                        <div className="pb-4 flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${meta.color}`}>
                              {meta.label}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">{dtStr}</span>
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{log.description}</p>
                          {log.justification && (
                            <div className="mt-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded px-3 py-1.5">
                              <p className="text-xs text-yellow-800 dark:text-yellow-300">
                                <span className="font-semibold">Justificativa:</span> {log.justification}
                              </p>
                            </div>
                          )}
                          {log.user_name && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">por {log.user_name}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              <button
                onClick={() => setHistoryModal(null)}
                className="w-full py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <GenerateInvoicesModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onSuccess={handleGenerateSuccess}
      />

      {/* ── Modal alterar vencimentos em lote ── */}
      {bulkDueDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">📅 Alterar Vencimentos</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{bulkDueDateRows.length} fatura(s) selecionada(s)</p>
              </div>
              <button onClick={() => setBulkDueDateModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl">✕</button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left pb-2">Fatura</th>
                    <th className="text-left pb-2">Cliente</th>
                    <th className="text-center pb-2">Venc. Atual</th>
                    <th className="text-center pb-2">Nova Data</th>
                    <th className="text-center pb-2">Atualizar Asaas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {bulkDueDateRows.map((row, i) => (
                    <tr key={row.id} className="py-2">
                      <td className="py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{row.invoice_number}</td>
                      <td className="py-2 text-xs text-gray-600 dark:text-gray-400 max-w-[140px] truncate">{row.client}</td>
                      <td className="py-2 text-center text-xs text-gray-500">{row.currentDate}</td>
                      <td className="py-2 text-center">
                        <input
                          type="date"
                          value={row.newDate}
                          onChange={e => setBulkDueDateRows(prev => prev.map((r, j) => j === i ? { ...r, newDate: e.target.value } : r))}
                          className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </td>
                      <td className="py-2 text-center">
                        {row.hasAsaas ? (
                          <input
                            type="checkbox"
                            checked={row.updateAsaas}
                            onChange={e => setBulkDueDateRows(prev => prev.map((r, j) => j === i ? { ...r, updateAsaas: e.target.checked } : r))}
                            className="w-4 h-4 rounded text-blue-600"
                            title="Atualizar cobrança no Asaas"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setBulkDueDateModal(false)} className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm">Cancelar</button>
              <button
                onClick={handleSaveBulkDueDate}
                disabled={savingBulkDueDate}
                className="px-4 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 text-sm font-semibold disabled:opacity-50"
              >{savingBulkDueDate ? 'Salvando...' : 'Salvar Vencimentos'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Status Notificação Extrajudicial ── */}
      {extrajudicialStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">⚖️ Status — Notificação Extrajudicial</h2>
                <p className="text-xs text-gray-500 mt-0.5">#{extrajudicialStatusModal.invoice.invoice_number} — {extrajudicialStatusModal.invoice.client_name}</p>
              </div>
              <button onClick={() => setExtrajudicialStatusModal(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            {extrajudicialStatusModal.loading ? (
              <div className="p-8 flex items-center justify-center">
                <div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full" />
              </div>
            ) : extrajudicialStatusModal.data ? (() => {
              const d = extrajudicialStatusModal.data;
              const statusLabel = {
                SIGNED: { label: 'Assinado', color: 'green', icon: '✅' },
                PENDING: { label: 'Aguardando assinatura', color: 'amber', icon: '⏳' },
                CANCELLED: { label: 'Cancelado', color: 'red', icon: '❌' },
              }[d.status] || { label: d.status, color: 'gray', icon: '❓' };
              return (
                <div className="p-5 space-y-4">
                  <div className={`flex items-center gap-3 p-4 rounded-lg ${
                    d.status === 'SIGNED' ? 'bg-green-50 dark:bg-green-900/20' :
                    d.status === 'CANCELLED' ? 'bg-red-50 dark:bg-red-900/20' :
                    'bg-amber-50 dark:bg-amber-900/20'
                  }`}>
                    <span className="text-2xl">{statusLabel.icon}</span>
                    <div>
                      <p className={`font-bold text-sm ${
                        d.status === 'SIGNED' ? 'text-green-800 dark:text-green-300' :
                        d.status === 'CANCELLED' ? 'text-red-800 dark:text-red-300' :
                        'text-amber-800 dark:text-amber-300'
                      }`}>{statusLabel.label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Enviado em {formatDate(d.sent_at)}
                      </p>
                    </div>
                  </div>

                  {d.signers && d.signers.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Signatários</p>
                      <div className="space-y-2">
                        {d.signers.map((s, i) => (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <div>
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{s.name}</p>
                              <p className="text-xs text-gray-500">{s.email}</p>
                            </div>
                            {s.signed ? (
                              <div className="text-right">
                                <span className="text-xs font-bold text-green-600 dark:text-green-400">✅ Assinado</span>
                                {s.signed_at && <p className="text-xs text-gray-400">{formatDate(s.signed_at)}</p>}
                              </div>
                            ) : (
                              <span className="text-xs font-bold text-amber-600 dark:text-amber-400">⏳ Pendente</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })() : null}

            <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setExtrajudicialStatusModal(null)}
                className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Notificação Extrajudicial ── */}
      {extrajudicialModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">⚖️ Notificação Extrajudicial</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Fatura #{extrajudicialModal.invoice_number} — {extrajudicialModal.client_name}
                </p>
              </div>
              <button onClick={() => { setExtrajudicialModal(null); setExtrajudicialData(null); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            {loadingExtrajudicial ? (
              <div className="p-8 flex items-center justify-center">
                <div className="animate-spin h-8 w-8 border-4 border-red-600 border-t-transparent rounded-full" />
              </div>
            ) : extrajudicialData ? (() => {
              const inv = extrajudicialData.invoice;
              const partner = extrajudicialData.companyPartner;
              const totalValue = parseFloat(inv.total_value) || 0;
              const dueDate = new Date(inv.due_date);
              const today = new Date();
              const daysOv = Math.max(0, Math.floor((today - dueDate) / (1000*60*60*24)));
              const multa = totalValue * 0.10;
              const juros = totalValue * 0.01 * (daysOv / 30);
              const totalDue = totalValue + multa + juros;
              const fmtC = (v) => formatCurrency(v);
              return (
                <div className="p-5 space-y-4">
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
                      Fatura vencida há {daysOv} dias
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400">
                      Vencimento: {formatDate(inv.due_date)}
                    </p>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Valor principal</span>
                      <span className="font-medium">{fmtC(totalValue)}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Multa contratual (10%)</span>
                      <span className="font-medium text-red-600">{fmtC(multa)}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Juros de mora (1%/mês — {daysOv} dias)</span>
                      <span className="font-medium text-red-600">{fmtC(juros)}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="font-bold text-gray-900 dark:text-white">TOTAL DEVIDO</span>
                      <span className="font-bold text-red-700 dark:text-red-400 text-lg">{fmtC(totalDue)}</span>
                    </div>
                  </div>

                  {partner ? (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-sm">
                      <p className="font-semibold text-blue-800 dark:text-blue-300 mb-1">Será enviado ao ClickSign para assinatura de:</p>
                      <p className="text-blue-700 dark:text-blue-400">{partner.name}</p>
                      <p className="text-blue-600 dark:text-blue-500 text-xs">{partner.email}{partner.cpf ? ` — CPF: ${partner.cpf}` : ''}</p>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-sm text-yellow-700 dark:text-yellow-400">
                      Nenhum sócio principal encontrado. Configure um sócio principal da empresa antes de enviar.
                    </div>
                  )}

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Um PDF da notificação extrajudicial será gerado e enviado ao ClickSign para assinatura digital do representante da empresa.
                  </p>
                </div>
              );
            })() : null}

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => { setExtrajudicialModal(null); setExtrajudicialData(null); }}
                className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm"
                disabled={sendingExtrajudicial}
              >
                Cancelar
              </button>
              {extrajudicialData?.companyPartner && (
                <button
                  onClick={handleSendExtrajudicial}
                  disabled={sendingExtrajudicial || loadingExtrajudicial}
                  className="px-4 py-2 rounded-lg bg-red-700 text-white hover:bg-red-800 text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                >
                  {sendingExtrajudicial ? (
                    <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> Enviando...</>
                  ) : '⚖️ Gerar PDF e Enviar via ClickSign'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const StatCard = ({ title, value, subtitle, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">{title}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{subtitle}</p>
          )}
        </div>
        <div className={`text-2xl sm:text-4xl ${colorClasses[color]} p-2 sm:p-3 rounded-xl flex-shrink-0 ml-2`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default InvoicesPage;
